import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GraphCentreType } from "#shared/schemas/graph.ts";

export interface GraphHop {
  type: GraphCentreType;
  id: string;
  label: string;
  /** How we arrived at this hop from the previous one. */
  link?: "step" | "jump";
}

interface GraphPathState {
  hops: GraphHop[];
  /** Index of the hop currently centred. Crumb clicks move this without truncating. */
  cursor: number;
  /** Forward branch discarded when stepping sideways from mid-trail. */
  discardedBranch: GraphHop[];
  /** Artists visited this session (and restored). */
  visitedIds: string[];
  genreFilterId: string | null;
  ringExpanded: boolean;
  selectedIds: string[];

  centre: () => GraphHop | null;
  previousHop: () => GraphHop | null;
  resetTo: (hop: GraphHop) => void;
  stepTo: (hop: GraphHop) => void;
  jumpTo: (index: number) => void;
  restoreDiscarded: () => void;
  clearPath: () => void;
  setGenreFilter: (id: string | null) => void;
  setRingExpanded: (expanded: boolean) => void;
  toggleSelected: (id: string) => void;
  selectRange: (ids: string[], fromId: string, toId: string) => void;
  setSelected: (ids: string[]) => void;
  clearSelection: () => void;
}

function rememberVisited(visited: string[], id: string): string[] {
  if (visited.includes(id)) return visited;
  const next = [...visited, id];
  return next.length > 80 ? next.slice(next.length - 80) : next;
}

/**
 * Graph research path — persists across reload. Crumb jumps keep forward hops;
 * stepping to a new node from mid-path stores the discarded branch for restore.
 */
export const useGraphPathStore = create<GraphPathState>()(
  persist(
    (set, get) => ({
      hops: [],
      cursor: 0,
      discardedBranch: [],
      visitedIds: [],
      genreFilterId: null,
      ringExpanded: false,
      selectedIds: [],

      centre: () => {
        const { hops, cursor } = get();
        return hops[cursor] ?? null;
      },

      previousHop: () => {
        const { hops, cursor } = get();
        return cursor > 0 ? (hops[cursor - 1] ?? null) : null;
      },

      resetTo: (hop) =>
        set({
          hops: [{ ...hop, link: hop.link ?? "step" }],
          cursor: 0,
          discardedBranch: [],
          visitedIds: rememberVisited([], hop.id),
          ringExpanded: false,
          selectedIds: [],
        }),

      stepTo: (hop) =>
        set((state) => {
          const at = state.hops[state.cursor];
          if (at && at.type === hop.type && at.id === hop.id) {
            return { selectedIds: [] };
          }
          const kept = state.hops.slice(0, state.cursor + 1);
          const discarded =
            state.cursor < state.hops.length - 1
              ? state.hops.slice(state.cursor + 1)
              : [];
          const hops = [...kept, { ...hop, link: hop.link ?? "step" }];
          return {
            hops,
            cursor: hops.length - 1,
            discardedBranch: discarded.length > 0 ? discarded : [],
            visitedIds: rememberVisited(state.visitedIds, hop.id),
            ringExpanded: false,
            selectedIds: [],
          };
        }),

      jumpTo: (index) =>
        set((state) => {
          if (index < 0 || index >= state.hops.length) return state;
          const hop = state.hops[index];
          if (!hop) return state;
          return {
            cursor: index,
            visitedIds: rememberVisited(state.visitedIds, hop.id),
            ringExpanded: false,
            selectedIds: [],
          };
        }),

      restoreDiscarded: () =>
        set((state) => {
          if (state.discardedBranch.length === 0) return state;
          const hops = [
            ...state.hops.slice(0, state.cursor + 1),
            ...state.discardedBranch,
          ];
          return {
            hops,
            discardedBranch: [],
            selectedIds: [],
          };
        }),

      clearPath: () =>
        set((state) => {
          const current = state.hops[state.cursor];
          if (!current) {
            return {
              hops: [],
              cursor: 0,
              discardedBranch: [],
              visitedIds: [],
              ringExpanded: false,
              selectedIds: [],
            };
          }
          return {
            hops: [{ ...current, link: "step" }],
            cursor: 0,
            discardedBranch: [],
            visitedIds: rememberVisited([], current.id),
            ringExpanded: false,
            selectedIds: [],
          };
        }),

      setGenreFilter: (genreFilterId) => set({ genreFilterId, selectedIds: [] }),

      setRingExpanded: (ringExpanded) => set({ ringExpanded }),

      toggleSelected: (id) =>
        set((state) => ({
          selectedIds: state.selectedIds.includes(id)
            ? state.selectedIds.filter((x) => x !== id)
            : [...state.selectedIds, id],
        })),

      selectRange: (ids, fromId, toId) => {
        const a = ids.indexOf(fromId);
        const b = ids.indexOf(toId);
        if (a < 0 || b < 0) return;
        const [lo, hi] = a < b ? [a, b] : [b, a];
        set({ selectedIds: ids.slice(lo, hi + 1) });
      },

      setSelected: (selectedIds) => set({ selectedIds }),

      clearSelection: () => set({ selectedIds: [] }),
    }),
    {
      name: "coykoi-graph-path",
      partialize: (s) => ({
        hops: s.hops,
        cursor: s.cursor,
        discardedBranch: s.discardedBranch,
        visitedIds: s.visitedIds,
        genreFilterId: s.genreFilterId,
      }),
    },
  ),
);
